import { Component, type ErrorInfo, type ReactNode } from 'react';
import { logCritical } from '@/lib/activityLog';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
}

/**
 * Captura errores de render de React y errores globales del navegador
 * (window.onerror y promesas rechazadas) y los registra en la auditoría
 * central, mostrando una pantalla amigable en lugar de una pantalla en blanco.
 */
export default class AppErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidMount() {
    window.addEventListener('error', this.handleWindowError);
    window.addEventListener('unhandledrejection', this.handleRejection);
  }

  componentWillUnmount() {
    window.removeEventListener('error', this.handleWindowError);
    window.removeEventListener('unhandledrejection', this.handleRejection);
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    logCritical('app', 'react_render_error', error.message || 'Error de render', {
      componentStack: (info.componentStack ?? '').slice(0, 1500),
    });
  }

  handleWindowError = (event: ErrorEvent) => {
    logCritical('app', 'window_error', event.message || 'Error de ejecución', {
      source: event.filename,
      line: event.lineno,
      column: event.colno,
    });
  };

  handleRejection = (event: PromiseRejectionEvent) => {
    const reason = event.reason;
    const message = reason instanceof Error ? reason.message : String(reason ?? 'Promesa rechazada');
    logCritical('app', 'unhandled_rejection', message);
  };

  handleReload = () => {
    this.setState({ hasError: false });
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-background-50 px-4">
          <div className="text-center max-w-md">
            <div className="w-16 h-16 mx-auto rounded-full bg-primary-100 text-primary-700 flex items-center justify-center">
              <i className="ri-error-warning-line text-3xl" />
            </div>
            <h1 className="mt-6 font-heading text-2xl font-bold text-foreground-950">
              Algo salió mal
            </h1>
            <p className="mt-3 text-foreground-600 leading-relaxed">
              Ocurrió un error inesperado. Recarga la página para continuar.
            </p>
            <button
              onClick={this.handleReload}
              className="mt-6 inline-flex items-center gap-2 rounded-md bg-primary-500 hover:bg-primary-600 px-4 py-2.5 text-sm font-semibold text-background-50 whitespace-nowrap transition cursor-pointer"
            >
              <i className="ri-refresh-line" /> Recargar
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}